import { z } from 'zod';
import {
  CommercialProposalModeSchema,
  CommercialProposalPaymentSchemeSchema,
  CommercialProposalTariffUnitSchema,
} from './status.js';

// WHY: calculated rouble money values cross the wire as canonical 2-decimal
// strings (never JS floats) so the Decimal precision computed by the
// backend (#1195) survives JSON round-tripping without binary-float drift.
// The string form is `-?\d+\.\d{2}`. The intermediate USD/CNY figures the
// public sheet displays use the same 2dp string form for consistency.
export const CP_MONEY_2DP_REGEX = /^-?\d+\.\d{2}$/;
const Money2dpString = z
  .string()
  .regex(CP_MONEY_2DP_REGEX, 'must be a decimal string with exactly 2 fraction digits');

/**
 * @aiPurpose pure numeric inputs to the proposal calculation (#1194).
 *   This is the subset of `CommercialProposalContent` the formula reads,
 *   projected so the calculation engine (#1195) has a typed, content-free
 *   contract. Defaults for the advanced coefficients match the prototype.
 * @aiInvariant `.strict()`; the authoritative formula is documented on
 *   `CommercialProposalCalculationOutputSchema` and pinned by the golden
 *   fixtures. The backend (#1195) executes it with Decimal + ROUND_HALF_UP.
 */
export const CommercialProposalCalculationInputSchema = z
  .object({
    /// КП composition (#1410); default BOTH gates which section(s) the engine
    /// computes. The output schema is unchanged — gated-out fields emit "0.00".
    mode: CommercialProposalModeSchema.default('BOTH'),
    paymentScheme: CommercialProposalPaymentSchemeSchema,
    cnyRateRub: z.number().finite().positive(),
    usdRateRub: z.number().finite().positive(),
    goodsCny: z.number().finite().nonnegative(),
    chinaDeliveryCny: z.number().finite().nonnegative(),
    servicePercent: z.number().finite().nonnegative(),
    weightKg: z.number().finite().nonnegative(),
    volumeM3: z.number().finite().nonnegative().default(0),
    tariffUsdPerKg: z.number().finite().nonnegative(),
    tariffUnit: CommercialProposalTariffUnitSchema.default('kg'),
    packagingUsd: z.number().finite().nonnegative(),
    prrUsd: z.number().finite().nonnegative(),
    insurancePercent: z.number().finite().nonnegative(),
    noVatDivisor: z.number().finite().positive().default(0.9),
    vatDivisor: z.number().finite().positive().default(0.8),
    insuranceCnyPerUsdRate: z.number().finite().positive().default(7.2),
  })
  .strict();
export type CommercialProposalCalculationInput = z.infer<
  typeof CommercialProposalCalculationInputSchema
>;

/**
 * @aiPurpose frozen calculated outputs of a proposal (#1194). The
 *   authoritative implementation is #1195 (Decimal + ROUND_HALF_UP); this
 *   schema is the wire/persistence shape. All money values are canonical
 *   2-decimal strings.
 *
 *   CANONICAL FORMULA — BOTH mode (per the prototype, encoded here as the contract):
 *     paymentDivisor   = cash/card/crypto -> 1 ; novat -> noVatDivisor ; vat -> vatDivisor
 *     serviceCny       = (goodsCny + chinaDeliveryCny) * servicePercent / 100
 *     totalCny         = goodsCny + chinaDeliveryCny + serviceCny
 *     buyRubFull       = totalCny * cnyRateRub / paymentDivisor
 *     buyRub           = ROUND_HALF_UP(buyRubFull, 2)
 *     transportUsd     = (tariffUnit === 'kg' ? weightKg : volumeM3) * tariffUsdPerKg
 *     insuranceUsd     = totalCny / insuranceCnyPerUsdRate * insurancePercent / 100
 *     logisticsUsd     = transportUsd + insuranceUsd + packagingUsd + prrUsd
 *     logisticsRubFull = logisticsUsd * usdRateRub / paymentDivisor
 *     logisticsRub     = ROUND_HALF_UP(logisticsRubFull, 2)
 *     grandTotalRub    = ROUND_HALF_UP(buyRubFull + logisticsRubFull, 2)
 *
 *   // tariffUnit (#1411): 'kg' uses weightKg, 'm3' uses volumeM3 as the freight multiplier; default 'kg'.
 *   MODE OVERRIDES (#1410): paymentDivisor resolves identically in every mode.
 *     BUYOUT    → transportUsd/insuranceUsd/logisticsUsd/logisticsRub = 0;
 *                 grandTotalRub = ROUND_HALF_UP(buyRubFull, 2).
 *     LOGISTICS → serviceCny = 0; totalCny = goodsCny (insurance basis is
 *                 goods-only, BR-7); buyRub = 0;
 *                 grandTotalRub = ROUND_HALF_UP(logisticsRubFull, 2).
 *     BOTH      → as listed above.
 *
 * @aiInvariant `grandTotalRub` rounds the SUM OF UNROUNDED active-row totals —
 *   it is NOT `buyRub + logisticsRub`. The two can differ by up to 0.01; the
 *   golden fixtures pin one such reconciliation case. `paymentDivisor` is
 *   echoed so the public sheet can label the scheme without recomputing.
 */
export const CommercialProposalCalculationOutputSchema = z
  .object({
    paymentDivisor: Money2dpString,
    serviceCny: Money2dpString,
    totalCny: Money2dpString,
    buyRub: Money2dpString,
    transportUsd: Money2dpString,
    insuranceUsd: Money2dpString,
    logisticsUsd: Money2dpString,
    logisticsRub: Money2dpString,
    grandTotalRub: Money2dpString,
  })
  .strict();
export type CommercialProposalCalculationOutput = z.infer<
  typeof CommercialProposalCalculationOutputSchema
>;

export { Money2dpString as CommercialProposalMoney2dpSchema };
